import { useCallback, useMemo } from 'react'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import useContentTag from 'hooks/Content/useContentTag'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { getContentTagIcon } from './contentTagIconUtils'
import MessageBanner, { type MessageBannerKind } from 'LuxKit/Banners/MessageBanner'
import type { ContentTagPage } from './contentTagTypes'

interface Props {
  slug: string
  tagName: string
  kind: MessageBannerKind
  page: ContentTagPage
}

/**
 * Renders a single ContentTag as a MessageBanner.
 */
function ContentTagBannerItem({ slug, tagName, kind, page }: Props) {
  const intl = useIntl()
  const { contentTag } = useContentTag(slug)
  const navigation = useAppNavigation()
  const location = useLocation()
  const isSearchPage = page === 'search'

  const isTagApplied = useMemo(() => {
    if (!isSearchPage) return false
    const currentTags = new URLSearchParams(location.search).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []
    return tagsArray.includes(tagName)
  }, [isSearchPage, location.search, tagName])

  const icon = useMemo(() => {
    if (!contentTag) {
      return undefined
    }
    return getContentTagIcon(contentTag.fields.icon)
  }, [contentTag])

  // Search page: clicking the button adds the tag to URL search params
  const handleSearchButtonClick = useCallback(() => {
    if (!isSearchPage) return

    const currentSearch = location.search
    const currentTags = new URLSearchParams(currentSearch).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []

    if (!tagsArray.includes(tagName)) {
      tagsArray.push(tagName)
    }

    const newSearch = setSearchParamValue(currentSearch, 'offerTags', tagsArray.join(','))
    navigation.push({
      ...location,
      search: newSearch,
    })
  }, [isSearchPage, tagName, navigation, location])

  const handleBackButtonClick = useCallback(() => {
    if (!isSearchPage) return

    const currentSearch = location.search
    const currentTags = new URLSearchParams(currentSearch).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []

    const newTagsArray = tagsArray.filter(tag => tag !== tagName)

    const newSearch = newTagsArray.length > 0
      ? setSearchParamValue(currentSearch, 'offerTags', newTagsArray.join(','))
      : setSearchParamValue(currentSearch, 'offerTags', '')

    navigation.push({
      ...location,
      search: newSearch,
    })
  }, [isSearchPage, tagName, navigation, location])

  if (!contentTag) {
    return null
  }

  const showActionButtons = isSearchPage && contentTag.fields.linkText

  const backLabel = intl.formatMessage({
    id: '_WmmEKsrsGXPj9VBwbIl',
    defaultMessage: 'Back',
  })

  const primaryActionButtonProps = showActionButtons ? {
    children: isTagApplied ? backLabel : contentTag.fields.linkText,
    onClick: isTagApplied ? handleBackButtonClick : handleSearchButtonClick,
  } : undefined

  return (
    <MessageBanner
      kind={kind}
      icon={icon}
      heading={contentTag.fields.title}
      primaryActionButtonProps={primaryActionButtonProps}
    >
      {contentTag.fields.subtitle}
    </MessageBanner>
  )
}

export default ContentTagBannerItem
